import { LiveAnnouncer } from "@angular/cdk/a11y";
import { AsyncPipe } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, input, OnInit, output, signal, viewChild } from "@angular/core";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { BiTranslateDirective, BiTranslatePipe, BiTranslateService } from "@globals/bi-translate";
import { CustomAnimations } from "@globals/classes/CustomAnimations";
import { maxLengthStreet } from "@globals/classes/global-constants";
import { BiAutofocusDirective } from "@globals/directives/bi-autofocus.directive";
import { BiCountryId } from "@globals/enums/BiLanguageAndCountryId";
import { isStringNullOrEmpty } from "@globals/helper-functions";
import { AddressModel } from "@globals/openapi-models/model/addressModel";
import { StreetNameReadModel } from "@globals/openapi-models/model/streetNameReadModel";
import { BiAddressServiceBase } from "@globals/services/address-base.service";
import { BiLocalizationHelperBaseService } from "@globals/services/bi-localization-helper-base.service";
import { BiSearchBaseService } from "@globals/services/bi-search-base.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { AutoComplete, AutoCompleteModule } from "primeng/autocomplete";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";
import { PanelModule } from "primeng/panel";
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, finalize, map, Observable, of, switchMap, take, tap } from "rxjs";
import BiZipCodeInputComponent from "../bi-zip-code-input/bi-zip-code-input.component";
import { BiSpinnerModule } from "../spinner/bi-spinner.module";
import { AddressSelectedEvent } from "./AddressSelectedEvent";

/**
 * Component with a form for entering address details and with inbuilt search feature.
 * Search results are shown as a dropdown list
 */
@UntilDestroy()
@Component({
  selector: "zip-street-number-input",
  imports: [
    FormsModule,
    ReactiveFormsModule,
    BiTranslatePipe,
    BiTranslateDirective,
    ButtonModule,
    PanelModule,
    InputTextModule,
    AutoCompleteModule,
    CardModule,
    BiZipCodeInputComponent,
    BiAutofocusDirective,
    BiSpinnerModule,
    AsyncPipe
  ],
  templateUrl: "./zip-street-number-input.component.html",
  styleUrls: ["./zip-street-number-input.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [CustomAnimations.fadeInDown],
  providers: [BiAddressServiceBase, BiSearchBaseService, BiLocalizationHelperBaseService]
})
export default class ZipStreetNumberInputComponent implements OnInit {
  readonly countryId = input<BiCountryId>(BiCountryId.DK);

  @Input() wcagCompliant = false;
  /**
   * Whether to show the list of found adresses. Default is true.
   */
  readonly showResultList = input(true);

  readonly buttonLabelTranslationKey = input<string>();

  readonly streetsFound = output<Array<StreetNameReadModel>>();

  readonly addressesFound = output<Array<AddressModel>>();

  readonly onAddressSelected = output<AddressSelectedEvent>();

  readonly streetAutoComplete = viewChild<AutoComplete>("autoComplete");

  readonly zipInput = viewChild<BiZipCodeInputComponent>("zipInput");

  // View Bindings
  protected addressForm: FormGroup;
  protected readonly showCityNotFoundError = signal(false);
  protected readonly cityForZip = signal("");
  protected readonly foundAddresses = signal<AddressModel[]>([]);
  protected readonly showNoAddressesFound = signal(false);
  /**
   * Autocomplete suggestions - filtered using the "streetsInZip"
   */
  public filteredStreets$: Observable<Array<StreetNameReadModel>> = of([]);
  private selectedStreet: StreetNameReadModel;

  protected readonly loadingAddresses = signal(false);

  protected readonly maxLengthStreet = maxLengthStreet;
  private zipCityMap: { [zip: string]: string } = {};

  /**
   * BehaviorSubject caching a list of street objects for a zipcode. The objects has street info containing street name and
   * a boolean telling whether there are any house numbers on that street.
   */
  private streetsInZip$ = new BehaviorSubject<Array<StreetNameReadModel>>([]);

  private readonly addressService = inject(BiAddressServiceBase);
  private readonly searchService = inject(BiSearchBaseService);
  private readonly announcer = inject(LiveAnnouncer);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly translator = inject(BiTranslateService);

  public ngOnInit() {
    this.addressForm = this.initAddressForm();

    this.observeZipCtrl();
    this.observeStreetCtrl();
    this.observeNumberCtrl();
  }

  private initAddressForm(): FormGroup {
    return new FormGroup({
      zipcode: new FormControl(null, Validators.required),
      street: new FormControl({ value: "", disabled: true }),
      houseNumber: new FormControl({ value: null, disabled: true })
    });
  }

  // Form Getters
  public get zipcodeCtrl() {
    return this.addressForm.get("zipcode");
  }
  public get streetCtrl() {
    return this.addressForm.get("street") as FormControl<StreetNameReadModel>;
  }
  public get houseNumberCtrl() {
    return this.addressForm.get("houseNumber");
  }

  // Form Subscribers
  private observeZipCtrl() {
    this.zipcodeCtrl.valueChanges
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        debounceTime(1000),
        tap(() => {
          // Reset inputs
          this.cityForZip.set("");
          this.streetsInZip$.next([]);
          this.filteredStreets$ = of([]);
          this.streetCtrl.reset();
          this.houseNumberCtrl.reset();
          this.showNoAddressesFound.set(false);
          this.foundAddresses.set([]);
        }),
        filter(_zip => this.zipcodeCtrl.valid),
        switchMap(zip => {
          // Check if already cached before making any call
          if (this.zipCityMap[zip.toString()]) return of(this.zipCityMap[zip.toString()]);
          else return this.addressService.getCityName(this.countryId(), zip);
        })
      )
      .subscribe(cityName => {
        // If city wasn't found, show error message
        if (!cityName) {
          this.showCityNotFoundError.set(true);
          this.cd.detectChanges();
          this.streetCtrl.disable();
          return;
        }

        this.announcer.announce(cityName);
        this.streetCtrl.enable();

        // Now we can get street names
        this.getStreetNames();

        this.showCityNotFoundError.set(false);
        // Automatically set the city name in the city control
        this.cityForZip.set(cityName);
        // If city name isn't cached, do it
        if (!this.zipCityMap[this.zipcodeCtrl.value.toString()]) this.zipCityMap[this.zipcodeCtrl.value.toString()] = cityName;

        this.cd.detectChanges(); // to make sure that view is updated (maybe errors was prsent before but should now be hidden)
      });

    this.zipcodeCtrl.statusChanges
      .pipe(
        untilDestroyed(this),
        filter(status => status === "VALID" || status === "INVALID")
      )
      .subscribe(status => (status === "VALID" ? this.streetCtrl.enable() : this.streetCtrl.disable()));
  }

  private observeStreetCtrl() {
    this.streetCtrl.valueChanges.pipe(distinctUntilChanged(), untilDestroyed(this)).subscribe((val: StreetNameReadModel) => {
      if (!val || isStringNullOrEmpty(val.streetName)) {
        this.houseNumberCtrl.disable();
      }

      this.filteredStreets$ = of([]);
      this.houseNumberCtrl.reset();
      this.showNoAddressesFound.set(false);
    });
  }

  private observeNumberCtrl() {
    this.houseNumberCtrl.valueChanges
      .pipe(
        distinctUntilChanged(),
        filter(val => val != null),
        debounceTime(1000),
        untilDestroyed(this)
      )
      .subscribe(() => this.getAddresses());
  }

  public onStreetSelected(street: StreetNameReadModel) {
    this.selectedStreet = street;

    if (!street.hasNumbers) {
      // Only 1 address - the street. Go get that address so we have its KVHX
      this.houseNumberCtrl.disable();
      this.getAddresses();
    } else {
      this.houseNumberCtrl.enable();
    }
  }

  public onStreetBlur() {
    setTimeout(() => {
      // If blurred from input while a value has benn selected and the current value is set to null, set the selected value again.
      // This fixes the bug in p-autoComplete component that doesn't support tab navigation for keyboard users
      if (!this.streetCtrl.value && this.selectedStreet) {
        this.streetCtrl.setValue(this.selectedStreet);
        this.houseNumberCtrl.enable();
      }

      if (!this.selectedStreet && this.streetCtrl.value) {
        this.getStreetsByName(this.streetCtrl.value.streetName).subscribe(streets => {
          if (streets && streets.length === 1) {
            this.onStreetSelected(streets[0]);
            this.streetCtrl.setValue(this.selectedStreet);
          }

          if (!this.selectedStreet && streets.length === 0) {
            this.streetCtrl.setErrors({ notFound: true });
          }
          this.cd.detectChanges();
        });
      }
    }, 200);
  }

  public onStreetsFound(streets: Array<StreetNameReadModel>) {
    this.streetsFound.emit(streets);
  }

  public onAddressesFound(addresses: Array<AddressModel>) {
    this.addressesFound.emit(addresses);
    this.foundAddresses.set(addresses);
    this.showNoAddressesFound.set(addresses.length === 0);
  }

  private getStreetsByName(name: string) {
    name = name?.replace(/^\s+|\s+$/gm, "");
    return this.streetsInZip$.pipe(
      filter(s => s.length > 0),
      take(1),
      map(streets => streets.filter(s => s.streetName.toLowerCase().lastIndexOf(name?.toLowerCase(), 0) === 0))
    );
  }

  public onStreetKeyUp(event: KeyboardEvent) {
    //https://www.toptal.com/developers/keycode/table-of-all-keycodes

    if (event.key === "Enter" || event.key === "Tab") return;

    if (
      this.selectedStreet &&
      (event.key === "Delete" ||
        event.key === "Backspace" ||
        (event.keyCode >= 48 && event.keyCode <= 57) || //0-9
        (event.keyCode >= 65 && event.keyCode <= 90) || //a-z
        (event.keyCode >= 97 && event.keyCode <= 122)) //0-9 (numpad)
    ) {
      // this.selectedStreet = null;
      this.cd.detectChanges();
    }
  }

  public addressSelected(address: AddressModel, target: EventTarget) {
    this.onAddressSelected.emit({ address: address, targetButton: target as HTMLButtonElement });
  }

  public searchStreets(streetQuery: string) {
    this.filteredStreets$ = this.streetsInZip$.pipe(
      filter(s => s.length > 0),
      take(1),
      map(streets => streets.filter(s => s.streetName.toLowerCase().lastIndexOf(streetQuery.toLowerCase(), 0) === 0)),
      tap(streets => {
        if (streets.length === 0) this.announcer.announce(this.translator.instant("errorMessages.StreetNotFound"));
      })
    );
  }

  public setStreetListAriaLabel() {
    setTimeout(() => document.getElementById(this.streetAutoComplete().id + "_list")?.setAttribute("aria-label", this.translator.instant("accessibility.ListWithStreetNames")), 100);
  }

  public reset() {
    this.addressForm.reset({
      zipcode: undefined,
      houseNumber: undefined,
      street: undefined
    });
    this.zipcodeCtrl.markAsPristine();
    this.streetCtrl.disable();
    this.showCityNotFoundError.set(false);
    this.houseNumberCtrl.disable();
    this.foundAddresses.set([]);
    this.showNoAddressesFound.set(false);
    this.cityForZip.set("");
    this.zipCityMap = {};
    this.streetsInZip$.next([]);
    document.getElementById(this.zipInput().uniqueInputId()).focus();
  }

  private getStreetNames() {
    this.searchService
      .getStreetNames(this.zipcodeCtrl.value, this.countryId())
      .pipe(untilDestroyed(this))
      .subscribe(streets => {
        this.streetsInZip$.next(streets);
        this.onStreetsFound(streets);
      });
  }

  private getAddresses() {
    if (this.addressForm.valid && (this.houseNumberCtrl.disabled || this.houseNumberCtrl.value != null)) {
      this.showNoAddressesFound.set(false);
      this.loadingAddresses.set(true);
      this.addressService
        .getAddresses(this.countryId(), this.zipcodeCtrl.value, this.streetCtrl.value.streetName, this.streetCtrl.value.streetId, this.houseNumberCtrl.value)
        .pipe(
          untilDestroyed(this),
          finalize(() => this.loadingAddresses.set(false))
        )
        .subscribe(addresses => this.onAddressesFound(addresses));
    } else this.foundAddresses.set([]);
  }
}
